import { computeClientAllocation } from '../utils/widgetInit/experimentCohorts';

export type ExperimentSampleSizeConfidence = 90 | 95 | 99;
export type ExperimentSampleSizePower = 80 | 90;

export type ExperimentSampleSizeWarningCode =
    | 'estimated_days_over_90'
    | 'control_visitors_under_100';

export type ExperimentSampleSizeWarningSeverity = 'hard' | 'soft';

export interface ExperimentSampleSizeWarning {
    code: ExperimentSampleSizeWarningCode;
    severity: ExperimentSampleSizeWarningSeverity;
    message: string;
}

export interface ExperimentSampleSizeInput {
    baseline_rate: number;
    daily_visitors_in_pool: number;
    mde_relative_pct: number;
    control_split_pct: number;
    confidence: ExperimentSampleSizeConfidence;
    power: ExperimentSampleSizePower;
}

/**
 * Sizing input for a continuous metric (e.g. time-on-page). A continuous
 * outcome has no "baseline rate"; its detectability is driven by the effect
 * size in standard-deviation units (Cohen's d), so we take the metric's mean
 * and standard deviation from the last 30 days and the smallest relative shift
 * in the mean worth detecting.
 */
export interface ContinuousSampleSizeInput {
    baseline_mean: number;
    baseline_sd: number;
    daily_visitors_in_pool: number;
    mde_relative_pct: number;
    control_split_pct: number;
    confidence: ExperimentSampleSizeConfidence;
    power: ExperimentSampleSizePower;
}

export interface ExperimentSampleSizeResult {
    n_per_cohort: {
        control: number;
        treatment: number;
    };
    total_n: number;
    estimated_days: number;
    warnings: ExperimentSampleSizeWarning[];
}

export interface StaffExperimentDailyVisitorsInput {
    eligible_domain_daily_totals: number[];
    traffic_percentage: number;
}

export interface ClientExperimentDailyVisitorsInput {
    daily_total: number;
    traffic_percentage: number;
    staff_holdout_pct: number;
    control_split_pct: number;
}

const Z_ALPHA: Record<ExperimentSampleSizeConfidence, number> = {
    90: 1.645,
    95: 1.96,
    99: 2.576,
};

const Z_BETA: Record<ExperimentSampleSizePower, number> = {
    80: 0.84,
    90: 1.282,
};

const MAX_ESTIMATED_DAYS = 90;
const MIN_DAILY_CONTROL_VISITORS = 100;

function assertFinitePositive(value: number, fieldName: string): void {
    if (!Number.isFinite(value) || value <= 0) {
        throw new RangeError(`${fieldName} must be a finite positive number.`);
    }
}

function assertPercentage(value: number, fieldName: string): void {
    if (!Number.isFinite(value) || value < 0 || value > 100) {
        throw new RangeError(`${fieldName} must be a finite percentage from 0 to 100.`);
    }
}

function assertSampleSizeInput(input: ExperimentSampleSizeInput): void {
    if (
        !Number.isFinite(input.baseline_rate) ||
        input.baseline_rate <= 0 ||
        input.baseline_rate >= 1
    ) {
        throw new RangeError(
            'baseline_rate must be a finite probability greater than 0 and less than 1.'
        );
    }

    assertFinitePositive(input.daily_visitors_in_pool, 'daily_visitors_in_pool');
    assertFinitePositive(input.mde_relative_pct, 'mde_relative_pct');

    if (
        !Number.isFinite(input.control_split_pct) ||
        input.control_split_pct <= 0 ||
        input.control_split_pct >= 100
    ) {
        throw new RangeError(
            'control_split_pct must be a finite percentage greater than 0 and less than 100.'
        );
    }

    const targetRate = input.baseline_rate * (1 + input.mde_relative_pct / 100);
    if (targetRate >= 1) {
        throw new RangeError(
            'baseline_rate and mde_relative_pct imply a target rate greater than or equal to 1.'
        );
    }
}

export function calculateExperimentSampleSize(
    input: ExperimentSampleSizeInput
): ExperimentSampleSizeResult {
    assertSampleSizeInput(input);

    const p1 = input.baseline_rate;
    const p2 = p1 * (1 + input.mde_relative_pct / 100);
    const q1 = 1 - p1;
    const q2 = 1 - p2;
    const k = (100 - input.control_split_pct) / input.control_split_pct;
    const za = Z_ALPHA[input.confidence];
    const zb = Z_BETA[input.power];
    const numerator = (za + zb) ** 2 * (p1 * q1 + (p2 * q2) / k);
    const denominator = (p2 - p1) ** 2;
    const nControl = Math.ceil(numerator / denominator);
    const nTreatment = Math.ceil(k * nControl);
    const totalN = nControl + nTreatment;
    const estimatedDays = totalN / input.daily_visitors_in_pool;
    const dailyControlVisitors = input.daily_visitors_in_pool * (input.control_split_pct / 100);
    const warnings: ExperimentSampleSizeWarning[] = [];

    if (estimatedDays > MAX_ESTIMATED_DAYS) {
        warnings.push({
            code: 'estimated_days_over_90',
            severity: 'hard',
            message:
                'Estimated run time is above 90 days. Raise MDE or control share before starting.',
        });
    }

    if (dailyControlVisitors < MIN_DAILY_CONTROL_VISITORS) {
        warnings.push({
            code: 'control_visitors_under_100',
            severity: 'soft',
            message:
                'Control cohort receives fewer than 100 visitors per day. Results may be noisy.',
        });
    }

    return {
        n_per_cohort: {
            control: nControl,
            treatment: nTreatment,
        },
        total_n: totalN,
        estimated_days: estimatedDays,
        warnings,
    };
}

/**
 * Planned total sample size (both arms) to detect the target MDE at the chosen
 * confidence/power, given the observed baseline. Independent of traffic rate —
 * used only to gate when the fixed-sample final read becomes valid. Returns null
 * when inputs are unusable.
 */
export function plannedTotalSampleSize(
    baselineRate: number | null | undefined,
    mdePct: number | null | undefined,
    controlSplitPct: number | null | undefined,
    confidence: ExperimentSampleSizeConfidence,
    power: ExperimentSampleSizePower
): number | null {
    if (baselineRate == null || mdePct == null || controlSplitPct == null) return null;
    try {
        return calculateExperimentSampleSize({
            baseline_rate: baselineRate,
            daily_visitors_in_pool: 1, // affects only estimated_days, not total_n
            mde_relative_pct: mdePct,
            control_split_pct: controlSplitPct,
            confidence,
            power,
        }).total_n;
    } catch {
        return null;
    }
}

/**
 * Smallest detectable MDE (relative %) at the given confidence/power for the
 * current observed pool. Binary search across 0.5..100%. Returns null when
 * inputs are unusable.
 */
export function smallestDetectableMde(
    baselineRate: number,
    dailyVisitorsInPool: number,
    currentTotalVisitors: number,
    controlSplitPct: number,
    confidence: ExperimentSampleSizeConfidence = 95,
    power: ExperimentSampleSizePower = 80
): number | null {
    if (baselineRate <= 0 || baselineRate >= 1) return null;
    if (dailyVisitorsInPool <= 0 || currentTotalVisitors <= 0) return null;

    let lo = 0.5;
    let hi = 100;
    let best: number | null = null;
    for (let i = 0; i < 30; i += 1) {
        const mid = (lo + hi) / 2;
        try {
            const r = calculateExperimentSampleSize({
                baseline_rate: baselineRate,
                daily_visitors_in_pool: dailyVisitorsInPool,
                mde_relative_pct: mid,
                control_split_pct: controlSplitPct,
                confidence,
                power,
            });
            if (r.total_n <= currentTotalVisitors) {
                best = mid;
                hi = mid;
            } else {
                lo = mid;
            }
        } catch {
            lo = mid;
        }
    }
    return best;
}

function buildSampleSizeWarnings(
    estimatedDays: number,
    dailyControlVisitors: number
): ExperimentSampleSizeWarning[] {
    const warnings: ExperimentSampleSizeWarning[] = [];
    if (estimatedDays > MAX_ESTIMATED_DAYS) {
        warnings.push({
            code: 'estimated_days_over_90',
            severity: 'hard',
            message:
                'Estimated run time is above 90 days. Raise MDE or control share before starting.',
        });
    }
    if (dailyControlVisitors < MIN_DAILY_CONTROL_VISITORS) {
        warnings.push({
            code: 'control_visitors_under_100',
            severity: 'soft',
            message:
                'Control cohort receives fewer than 100 visitors per day. Results may be noisy.',
        });
    }
    return warnings;
}

function assertContinuousSampleSizeInput(input: ContinuousSampleSizeInput): void {
    assertFinitePositive(input.baseline_mean, 'baseline_mean');
    assertFinitePositive(input.baseline_sd, 'baseline_sd');
    assertFinitePositive(input.daily_visitors_in_pool, 'daily_visitors_in_pool');
    assertFinitePositive(input.mde_relative_pct, 'mde_relative_pct');

    if (
        !Number.isFinite(input.control_split_pct) ||
        input.control_split_pct <= 0 ||
        input.control_split_pct >= 100
    ) {
        throw new RangeError(
            'control_split_pct must be a finite percentage greater than 0 and less than 100.'
        );
    }
}

/**
 * Sample size for a two-sample comparison of means (continuous metric).
 *
 * Effect size in SD units is Cohen's d = Δ / σ, where Δ is the smallest mean
 * shift worth detecting. Expressing Δ as a relative change of the baseline mean
 * gives d = (mde/100) · mean / sd. Per-cohort sizes follow the standard
 * two-mean formula with allocation ratio k = treatment / control:
 *
 *   n_control = (1 + 1/k) · (z_alpha + z_beta)² / d²
 *
 * which reduces to the familiar 2·((z_alpha + z_beta)/d)² per group at 50/50.
 *
 * This is the same normal-approximation a t-test would use. Session duration is
 * heavy-tailed and the live read uses Mann-Whitney, so treat the forecast as a
 * ballpark — its value is showing that a continuous metric needs far fewer
 * visitors than a low-baseline conversion, not predicting an exact day count.
 */
export function calculateContinuousSampleSize(
    input: ContinuousSampleSizeInput
): ExperimentSampleSizeResult {
    assertContinuousSampleSizeInput(input);

    const cohensD = (input.mde_relative_pct / 100) * (input.baseline_mean / input.baseline_sd);
    const k = (100 - input.control_split_pct) / input.control_split_pct;
    const za = Z_ALPHA[input.confidence];
    const zb = Z_BETA[input.power];
    const nControl = Math.ceil(((1 + 1 / k) * (za + zb) ** 2) / cohensD ** 2);
    const nTreatment = Math.ceil(k * nControl);
    const totalN = nControl + nTreatment;
    const estimatedDays = totalN / input.daily_visitors_in_pool;
    const dailyControlVisitors = input.daily_visitors_in_pool * (input.control_split_pct / 100);

    return {
        n_per_cohort: {
            control: nControl,
            treatment: nTreatment,
        },
        total_n: totalN,
        estimated_days: estimatedDays,
        warnings: buildSampleSizeWarnings(estimatedDays, dailyControlVisitors),
    };
}

export function estimateStaffExperimentDailyVisitors({
    eligible_domain_daily_totals,
    traffic_percentage,
}: StaffExperimentDailyVisitorsInput): number {
    assertPercentage(traffic_percentage, 'traffic_percentage');

    const dailyTotal = eligible_domain_daily_totals.reduce((sum, dailyVisitors) => {
        if (!Number.isFinite(dailyVisitors) || dailyVisitors < 0) {
            throw new RangeError(
                'eligible_domain_daily_totals must contain only finite non-negative numbers.'
            );
        }

        return sum + dailyVisitors;
    }, 0);

    return dailyTotal * (traffic_percentage / 100);
}

/** Rolling window (days) backing the per-client 30-day stats. */
const CLIENT_STATS_WINDOW_DAYS = 30;

export interface StaffForecastClientStat {
    widget_visitors: number | null | undefined;
    converted_persons: number | null | undefined;
}

/**
 * Derive the two forecast inputs a staff cross-site experiment needs — a daily
 * visitor pool and a baseline conversion rate — by summing the recent stats of
 * the selected clients.
 *
 * Both quantities share one denominator (widget displays over the last 30 days):
 *
 *   daily_visitors_in_pool = Σ widget_visitors / 30
 *   baseline_rate          = Σ converted_persons / Σ widget_visitors
 *
 * Returns null when the pool is empty or the baseline is degenerate (≤0 or ≥1),
 * which is what makes the sample-size calculator unusable — callers render an
 * em-dash / "not enough data" notice instead of forcing a throw.
 */
export function aggregateStaffForecastInputs(
    rows: StaffForecastClientStat[]
): { baseline_rate: number; daily_visitors_in_pool: number } | null {
    let totalWidgetVisitors = 0;
    let totalConverted = 0;
    for (const row of rows) {
        const visitors = Number(row.widget_visitors);
        const converted = Number(row.converted_persons);
        if (Number.isFinite(visitors) && visitors > 0) totalWidgetVisitors += visitors;
        if (Number.isFinite(converted) && converted > 0) totalConverted += converted;
    }

    if (totalWidgetVisitors <= 0) return null;

    const baselineRate = totalConverted / totalWidgetVisitors;
    if (!(baselineRate > 0 && baselineRate < 1)) return null;

    return {
        baseline_rate: baselineRate,
        daily_visitors_in_pool: totalWidgetVisitors / CLIENT_STATS_WINDOW_DAYS,
    };
}

export function estimateClientExperimentDailyVisitorsInPool({
    daily_total,
    traffic_percentage,
    staff_holdout_pct,
    control_split_pct,
}: ClientExperimentDailyVisitorsInput): number {
    if (!Number.isFinite(daily_total) || daily_total < 0) {
        throw new RangeError('daily_total must be a finite non-negative number.');
    }

    assertPercentage(traffic_percentage, 'traffic_percentage');
    assertPercentage(staff_holdout_pct, 'staff_holdout_pct');
    assertPercentage(control_split_pct, 'control_split_pct');

    // Carve model (IX-3984): the experiment pool is treatment + control =
    // ceiling (display + control), not just the displayed traffic. Control is
    // drawn from the non-displayed leftover, so it adds to the pool on top of
    // the full traffic display. Staff holdout still shrinks the pool first.
    const { ceilingBuckets } = computeClientAllocation(traffic_percentage, control_split_pct);
    return daily_total * (ceilingBuckets / 100) * (1 - staff_holdout_pct / 100);
}

/**
 * The % of all visitors who actually SEE Rose (treatment span) under the carve
 * model. Equals the configured traffic % until control cannot fit in the
 * non-displayed leftover (traffic near 100, or high traffic × high split), at
 * which point display shrinks to make room. UI uses this to warn when a chosen
 * control split would reduce the client's effective rollout below traffic %.
 */
export function clientExperimentDisplayPct(
    traffic_percentage: number,
    control_split_pct: number
): number {
    return computeClientAllocation(traffic_percentage, control_split_pct).displayBuckets;
}

/** The % of all visitors held back as A/B control under the carve model. */
export function clientExperimentControlPct(
    traffic_percentage: number,
    control_split_pct: number
): number {
    return computeClientAllocation(traffic_percentage, control_split_pct).controlBuckets;
}

/**
 * Control's share of the experiment POOL (control + treatment), as a percentage.
 * Under the carve model the operator's `control_split_pct` is control as a share
 * of allocated traffic; the sample-size calculator needs control as a share of
 * the pool (control / (control + display)). Returns 0 for an empty pool.
 */
export function clientExperimentPoolControlSplitPct(
    traffic_percentage: number,
    control_split_pct: number
): number {
    const { controlBuckets, ceilingBuckets } = computeClientAllocation(
        traffic_percentage,
        control_split_pct
    );
    return ceilingBuckets > 0 ? (controlBuckets / ceilingBuckets) * 100 : 0;
}
